import { AxiosResponse } from "axios";

import { IApiErrorResponse } from "../@types/ApiError";
import { HttpError } from "./HttpError";

/**
 * Shared blob-download plumbing for the /admin pages migrated from Retool
 * (EN-5148). Retool wrapped binary responses in a `{ base64Data, type }`
 * envelope its JS then decoded — that envelope is a Retool transport detail,
 * NOT the API contract: the export endpoints return a real file with a
 * Content-Disposition header, so the portal asks axios for a blob and saves
 * it directly. Do not port `base64ToArrayBuffer` from the exports.
 */

/**
 * Filename the server asked us to save the file as, from its
 * Content-Disposition header. Handles the encoded form
 * (`filename*=UTF-8''pMp%20Report.xlsx`, which .NET emits whenever the name
 * isn't pure ASCII) as well as quoted and bare `filename=`.
 */
export function parseContentDispositionFilename(
  headerValue: unknown
): string | undefined {
  if (typeof headerValue !== "string" || headerValue.length === 0) {
    return undefined;
  }

  const encoded = headerValue.match(/filename\*=\s*UTF-8''([^;]+)/i);
  if (encoded) {
    const value = encoded[1].trim();
    try {
      return decodeURIComponent(value) || undefined;
    } catch {
      return value || undefined;
    }
  }

  const quoted = headerValue.match(/filename\s*=\s*"([^"]*)"/i);
  if (quoted) {
    return quoted[1].trim() || undefined;
  }

  const bare = headerValue.match(/filename\s*=\s*([^;]+)/i);
  return bare ? bare[1].trim() || undefined : undefined;
}

/** Hands a blob to the browser's downloader under the given name. */
export function saveBlobAs(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = Object.assign(document.createElement("a"), {
    href: url,
    download: filename,
  });

  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function readBlobWithFileReader(blob: Blob): Promise<string | undefined> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === "string" ? reader.result : undefined);
    reader.onerror = () => resolve(undefined);
    reader.readAsText(blob);
  });
}

async function blobToText(data: unknown): Promise<string | undefined> {
  if (typeof data === "string") return data;
  if (!(data instanceof Blob)) return undefined;

  try {
    // Blob.text() is missing in jsdom and in Safari before 14, so fall back
    // to FileReader rather than lose the server's error message.
    if (typeof data.text === "function") return await data.text();
    return await readBlobWithFileReader(data);
  } catch {
    return undefined;
  }
}

/**
 * Best-effort error message out of a blob body. Error responses to a
 * `responseType: "blob"` request arrive as a Blob, not parsed JSON, so
 * `ErrorResponse.errors` has to be read back out of the text.
 */
async function readErrorMessage(data: unknown): Promise<string | undefined> {
  const text = await blobToText(data);
  if (!text) return undefined;

  try {
    const parsed = JSON.parse(text) as IApiErrorResponse;
    const message = parsed?.errors?.find((error) => error.message)?.message;
    return message || undefined;
  } catch {
    return undefined;
  }
}

/**
 * True when a 2xx body is actually a JSON error document. The API's export
 * actions always return a real file, so a JSON body carrying `errors` on a
 * "successful" response means something went wrong upstream — saving it
 * would hand the user an .xlsx full of JSON.
 */
async function isJsonErrorPayload(data: unknown): Promise<boolean> {
  if (!(data instanceof Blob)) return false;
  if (!data.type.includes("json")) return false;

  const text = await blobToText(data);
  if (!text) return false;

  try {
    const parsed = JSON.parse(text) as IApiErrorResponse;
    return Array.isArray(parsed?.errors);
  } catch {
    return false;
  }
}

interface IDownloadFromResponseOptions {
  /** Used when the server sends no Content-Disposition filename. */
  fallbackFilename: string;
}

/**
 * Saves the file carried by a blob response, returning the filename used.
 *
 * Throws `HttpError` instead of writing a file when the request failed. This
 * guard is not optional: the shared axios interceptor resolves non-2xx
 * responses as if they had succeeded (`services/api.ts` → `return
 * error.response`), so without it a 400/403 would be saved as an .xlsx
 * containing the error JSON. A rejected request with no response at all
 * (network/CORS) resolves to `undefined` for the same reason.
 */
export async function downloadFromResponse(
  response: AxiosResponse<Blob> | undefined,
  { fallbackFilename }: IDownloadFromResponseOptions
): Promise<string> {
  if (!response) {
    throw new HttpError(0, "No response received from the server");
  }

  const status = response.status ?? 0;
  if (status < 200 || status >= 300) {
    const message = await readErrorMessage(response.data);
    throw new HttpError(status, message ?? `Download failed: ${status}`);
  }

  if (await isJsonErrorPayload(response.data)) {
    const message = await readErrorMessage(response.data);
    throw new HttpError(status, message ?? "The server returned no file");
  }

  const filename =
    parseContentDispositionFilename(
      response.headers?.["content-disposition"]
    ) ?? fallbackFilename;

  saveBlobAs(
    response.data instanceof Blob ? response.data : new Blob([response.data]),
    filename
  );

  return filename;
}
